import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import { FormattedMessage, useIntl } from 'react-intl'

import { getOrganizationByName } from '../config/organizations'
import { getSelectedOrganization } from '../services/api/resolveApiBaseUrl'
import OrganizationStorage from '../services/storage/organization-storage/OrganizationStorage'
import { performLogoutCleanup } from '../services/auth/performLogoutCleanup'

/**
 * Shared "change organization" dialog — the seller types the organization name
 * they were given rather than picking from a list. Used from both the app drawer
 * (post-login) and the login screen (so the org is visible/changeable before a
 * seller even tries to log in, not just buried in the drawer).
 */
export function useOrganizationSwitchDialog() {
  const intl = useIntl()
  const selectedOrganization = getSelectedOrganization()
  const [isOpen, setIsOpen] = useState(false)
  const [pendingOrganizationName, setPendingOrganizationName] = useState('')
  const [isChanging, setIsChanging] = useState(false)
  const [error, setError] = useState(false)

  const open = () => {
    setPendingOrganizationName(selectedOrganization?.name ?? '')
    setError(false)
    setIsOpen(true)
  }

  const handleClose = () => {
    if (isChanging) return
    setIsOpen(false)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingOrganizationName(event.target.value)
    setError(false)
  }

  const handleConfirm = async () => {
    // Guards against a double-tap setting the organization twice or reloading
    // mid-removal, since performLogoutCleanup is async.
    if (!pendingOrganizationName || isChanging) return

    const organization = getOrganizationByName(pendingOrganizationName)
    if (!organization) {
      setError(true)
      return
    }

    setIsChanging(true)
    try {
      await performLogoutCleanup()
      OrganizationStorage.set(organization.id)
      window.location.reload()
      // Deliberately not resetting isChanging on success — the reload is about to
      // unmount this component, and resetting first would let the dialog flash
      // back to an interactive state right before that happens.
    } catch (submitError) {
      // Reset so a transient cleanup failure doesn't leave the confirm button
      // disabled forever with no way to retry.
      console.error('[useOrganizationSwitchDialog] failed to change organization', submitError)
      setIsChanging(false)
    }
  }

  const dialog = (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      // The theme's dark palette (background.paper: '#000') plus MUI's dark-mode
      // elevation overlay is opaque in practice, but only as an emergent result of
      // several theme settings combining — explicit here so the dialog can never
      // render see-through regardless of future theme changes.
      PaperProps={{ sx: { backgroundColor: '#1e1e1e', backgroundImage: 'none' } }}
    >
      <DialogTitle>
        <FormattedMessage
          id='app.components.app-drawer.organization.dialog-title'
          defaultMessage='Change organization'
        />
      </DialogTitle>
      <DialogContent>
        {/* Explicit color: DialogContentText defaults to theme text.secondary, which
            this theme sets to '#000' (see theme/index.ts) for use on the app's light
            panels. On this dark dialog that renders black-on-charcoal — invisible.
            Reproduced on-device. */}
        <DialogContentText sx={{ mb: 2, color: '#c9cacd' }}>
          <FormattedMessage
            id='app.components.app-drawer.organization.dialog-warning'
            defaultMessage='Switching organizations will log you out. You will need to log in again against the new organization.'
          />
        </DialogContentText>
        {/* A static caption above the field, not TextField's floating `label` —
            the theme's MuiOutlinedInput override puts a solid border directly on
            the input root (see theme/index.ts), which breaks the notch the
            floating label normally cuts into that border. The label ended up
            straddling the dark dialog background and the white field with no
            single background to contrast against, unreadable either side of
            the seam. Reproduced on-device; this sidesteps the mechanism instead
            of patching it. */}
        {/* Placeholder + leading icon, matching the login/organization screens'
            field style — no floating `label`, which the theme's MuiOutlinedInput
            border override renders unreadable across the border seam. */}
        <TextField
          fullWidth
          autoCapitalize='none'
          autoCorrect='off'
          placeholder={intl.formatMessage({
            id: 'app.components.app-drawer.organization',
            defaultMessage: 'Organization',
          })}
          value={pendingOrganizationName}
          onChange={handleChange}
          disabled={isChanging}
          error={error}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start' disablePointerEvents>
                <BusinessIcon sx={{ fontSize: '1.25rem', color: '#000' }} />
              </InputAdornment>
            ),
          }}
          helperText={
            error
              ? intl.formatMessage({
                id: 'app.components.organization-picker.not-found',
                defaultMessage: 'Organization not found',
              })
              : ' '
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isChanging}>
          <FormattedMessage
            id='app.components.app-drawer.organization.cancel'
            defaultMessage='Cancel'
          />
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!pendingOrganizationName || isChanging}
          color='primary'
          variant='contained'
        >
          <FormattedMessage
            id='app.components.app-drawer.organization.confirm'
            defaultMessage='Change'
          />
        </Button>
      </DialogActions>
    </Dialog>
  )

  return { selectedOrganization, open, dialog }
}
